import { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Row,
  Col,
  Card,
  Space,
  Select,
  DatePicker,
  TimePicker,
  Tag,
  Drawer,
  Descriptions,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  EyeOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { scheduleAPI, screenAPI, playlistAPI } from '../services/api';
import { themeColors } from '../config/theme';
import { useAuthStore } from '../store/authStore';

const parseTimeSlots = (timeSlots) => {
  if (typeof timeSlots === 'string') {
    try {
      timeSlots = JSON.parse(timeSlots);
    } catch {
      timeSlots = null;
    }
  }
  return Array.isArray(timeSlots) ? timeSlots : [];
};

export default function Schedules() {
  const isSubAdmin = useAuthStore((state) => state.user?.role === 'sub_admin');
  const [schedules, setSchedules] = useState([]);
  const [screens, setScreens] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [filter, setFilter] = useState('all');
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [detailDrawer, setDetailDrawer] = useState({ open: false, record: null });

  useEffect(() => {
    Promise.all([fetchSchedules(), fetchScreens(), fetchPlaylists()]);
  }, [pagination, filter]);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (filter !== 'all') {
        params.filters = JSON.stringify({ status: filter });
      }

      const response = await scheduleAPI.list(params);
      setSchedules(response.data.data.data);
      setTotal(response.data.data.pagination.total);
    } catch (error) {
      message.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const fetchScreens = async () => {
    try {
      const response = await screenAPI.list({ limit: 100 });
      setScreens(response.data.data.data);
    } catch (error) {
      console.error('Failed to load screens');
    }
  };

  const fetchPlaylists = async () => {
    try {
      const response = await playlistAPI.list({ limit: 100 });
      setPlaylists(response.data.data.data);
    } catch (error) {
      console.error('Failed to load playlists');
    }
  };

  const handleCreateOrUpdate = async (values) => {
    try {
      const timeSlots = (values.timeSlots || [])
        .filter((slot) => slot && slot.range)
        .map((slot) => ({
          start: slot.range[0].format('HH:mm'),
          end: slot.range[1].format('HH:mm'),
        }));

      const payload = {
        screenId: values.screen_id,
        playlistId: values.playlist_id,
        startDatetime: values.dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
        endDatetime: values.dateRange[1].format('YYYY-MM-DD HH:mm:ss'),
        priority: values.priority || 0,
        timeSlots,
      };

      if (editingId) {
        await scheduleAPI.update(editingId, payload);
        message.success('Schedule updated successfully');
      } else {
        await scheduleAPI.create(payload);
        message.success('Schedule created successfully');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingId(null);
      fetchSchedules();
    } catch (error) {
      message.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      screen_id: record.screen_id,
      playlist_id: record.playlist_id,
      priority: record.priority,
      dateRange: [
        dayjs(record.start_datetime),
        dayjs(record.end_datetime),
      ],
      timeSlots: parseTimeSlots(record.time_slots).map((slot) => ({
        range: [dayjs(slot.start, 'HH:mm'), dayjs(slot.end, 'HH:mm')],
      })),
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Schedule',
      content: 'Are you sure you want to delete this schedule?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await scheduleAPI.delete(id);
          message.success('Schedule deleted successfully');
          fetchSchedules();
        } catch (error) {
          message.error('Failed to delete schedule');
        }
      },
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'blue',
      active: 'green',
      expired: 'red',
    };
    return colors[status] || 'default';
  };

  const getScreenName = (screenId) => {
    return screens.find((s) => s.id === screenId)?.screen_name || `Screen ${screenId}`;
  };

  const getPlaylistName = (playlistId) => {
    return playlists.find((p) => p.id === playlistId)?.name || `Playlist ${playlistId}`;
  };

  const columns = [
    {
      title: 'Screen',
      dataIndex: 'screen_id',
      key: 'screen_id',
      render: (screenId) => getScreenName(screenId),
      width: 150,
    },
    {
      title: 'Playlist',
      dataIndex: 'playlist_id',
      key: 'playlist_id',
      render: (playlistId) => getPlaylistName(playlistId),
      width: 150,
    },
    {
      title: 'Start Date',
      dataIndex: 'start_datetime',
      key: 'start_datetime',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
      width: 150,
    },
    {
      title: 'End Date',
      dataIndex: 'end_datetime',
      key: 'end_datetime',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
      width: 150,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
    },
    {
      title: 'Time Slots',
      dataIndex: 'time_slots',
      key: 'time_slots',
      width: 180,
      render: (value) => {
        const timeSlots = parseTimeSlots(value);
        return timeSlots.length > 0 ? (
          <Space size={[4, 4]} wrap>
            {timeSlots.map((slot, i) => (
              <Tag key={i} icon={<ClockCircleOutlined />} color="purple">
                {slot.start} - {slot.end}
              </Tag>
            ))}
          </Space>
        ) : (
          <Tag>All Day</Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>,
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setDetailDrawer({ open: true, record })}
          />
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        {!isSubAdmin && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingId(null);
                form.resetFields();
                setIsModalOpen(true);
              }}
            >
              Create Schedule
            </Button>
          </Col>
        )}
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchSchedules} loading={loading} />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
            <div style={{ color: themeColors.textSecondary, fontSize: '12px' }}>Scheduled</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: themeColors.info }}>
              {schedules.filter((s) => s.status === 'scheduled').length}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
            <div style={{ color: themeColors.textSecondary, fontSize: '12px' }}>Active</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: themeColors.success }}>
              {schedules.filter((s) => s.status === 'active').length}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
            <div style={{ color: themeColors.textSecondary, fontSize: '12px' }}>Expired</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: themeColors.error }}>
              {schedules.filter((s) => s.status === 'expired').length}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
            <div style={{ color: themeColors.textSecondary, fontSize: '12px' }}>Total</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: themeColors.primary }}>
              {total}
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={6}>
          <Button
            block
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Button
            block
            type={filter === 'scheduled' ? 'primary' : 'default'}
            onClick={() => setFilter('scheduled')}
          >
            Scheduled
          </Button>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Button
            block
            type={filter === 'active' ? 'primary' : 'default'}
            onClick={() => setFilter('active')}
          >
            Active
          </Button>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Button
            block
            type={filter === 'expired' ? 'primary' : 'default'}
            onClick={() => setFilter('expired')}
          >
            Expired
          </Button>
        </Col>
      </Row>

      <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
        <Table
          columns={columns}
          dataSource={schedules}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total,
            onChange: (page, pageSize) => {
              setPagination({ page, limit: pageSize });
            },
          }}
          rowKey="id"
          style={{ color: themeColors.text }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingId ? 'Edit Schedule' : 'Create Schedule'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setEditingId(null);
        }}
        style={{ backgroundColor: themeColors.bgElevated }}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateOrUpdate}
          style={{ marginTop: '20px' }}
        >
          <Form.Item
            name="screen_id"
            label="Select Screen"
            rules={[{ required: true, message: 'Please select a screen' }]}
          >
            <Select
              placeholder="Choose a screen..."
              options={screens.map((s) => ({
                label: `${s.screen_name} (${s.screen_code})`,
                value: s.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="playlist_id"
            label="Select Playlist"
            rules={[{ required: true, message: 'Please select a playlist' }]}
          >
            <Select
              placeholder="Choose a playlist..."
              options={playlists.map((p) => ({
                label: p.name,
                value: p.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Schedule Period"
            rules={[{ required: true, message: 'Please select start and end date/time' }]}
          >
            <DatePicker.RangePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="priority"
            label="Priority (higher = plays first)"
            initialValue={0}
          >
            <Input type="number" min={0} placeholder="0" />
          </Form.Item>

          <Form.Item label="Daily Time Slots (optional - leave empty to play all day)">
            <Form.List name="timeSlots">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'range']}
                        rules={[{ required: true, message: 'Select a time range' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <TimePicker.RangePicker format="HH:mm" />
                      </Form.Item>
                      <Button danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                    Add Time Slot
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title="Schedule Details"
        placement="right"
        onClose={() => setDetailDrawer({ open: false, record: null })}
        open={detailDrawer.open}
        width={400}
      >
        {detailDrawer.record && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Screen">
              {getScreenName(detailDrawer.record.screen_id)}
            </Descriptions.Item>
            <Descriptions.Item label="Playlist">
              {getPlaylistName(detailDrawer.record.playlist_id)}
            </Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {dayjs(detailDrawer.record.start_datetime).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              {dayjs(detailDrawer.record.end_datetime).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Priority">
              {detailDrawer.record.priority}
            </Descriptions.Item>
            <Descriptions.Item label="Time Slots">
              {parseTimeSlots(detailDrawer.record.time_slots).length > 0 ? (
                <Space size={[4, 4]} wrap>
                  {parseTimeSlots(detailDrawer.record.time_slots).map((slot, i) => (
                    <Tag key={i} icon={<ClockCircleOutlined />} color="purple">
                      {slot.start} - {slot.end}
                    </Tag>
                  ))}
                </Space>
              ) : (
                'All Day'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(detailDrawer.record.status)}>
                {detailDrawer.record.status.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Duration">
              {dayjs(detailDrawer.record.end_datetime).diff(
                dayjs(detailDrawer.record.start_datetime),
                'day'
              ) + 1}{' '}
              day(s)
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {dayjs(detailDrawer.record.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
}
